import { DeskCalendarEvent } from "types/Desk";

/**
 * Display helpers for the meeting invite parsed out of an email's .ics part
 * (backend: DeskCalendarService). A Teams/Outlook invite says nothing about
 * time in its visible body, so this is the only place the card can get it.
 *
 * Everything is rendered in Vilnius, like every other date in the app — a card
 * opened abroad must show the same hour as the one the colleague sees here.
 */
const TIME_ZONE = "Europe/Vilnius";

export interface MeetingDisplay {
  /** "2026-09-22" in the user's locale, e.g. "2026-09-22" / "rugsėjo 22 d." */
  date: string;
  /** "09:00–10:00", or "09:00" with no end, or "" for an all-day event. */
  time: string;
  /** The zone the times are in — the resolved IANA zone or the raw ICS label. */
  zone: string | null;
  /** True when we could not resolve the invite's zone: times are as written. */
  isFloating: boolean;
  isCancelled: boolean;
}

/** "2026-09-22T09:00:00" → its date and HH:mm halves, no conversion. */
function splitLocal(local: string): { date: string; time: string } {
  const [date, time] = local.split("T");
  return { date: date, time: time ? time.slice(0, 5) : "" };
}

function fmtParts(ms: number, locale: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
}

export function describeMeeting(event: DeskCalendarEvent, locale = "lt-LT"): MeetingDisplay {
  const isCancelled = event.method === "CANCEL" || event.status === "CANCELLED";

  if (event.allDay) {
    const start = splitLocal(event.startLocal);
    const end = event.endLocal ? splitLocal(event.endLocal) : null;
    return {
      date: end && end.date !== start.date ? `${start.date} – ${end.date}` : start.date,
      time: "",
      zone: null,
      isFloating: false,
      isCancelled: isCancelled,
    };
  }

  // No absolute instant: the invite used a zone we refuse to guess at, so the
  // wall clock is shown exactly as written, next to the zone name it came with.
  if (event.startAt === null || event.startAt === undefined) {
    const start = splitLocal(event.startLocal);
    const end = event.endLocal ? splitLocal(event.endLocal) : null;
    return {
      // An unresolvable zone is no reason to hide that the meeting runs past
      // midnight — "23:00–01:00" on one date would read as a 22-hour meeting.
      date: !end || end.date === start.date ? start.date : `${start.date} – ${end.date}`,
      time: end ? `${start.time}–${end.time}` : start.time,
      zone: event.tzid,
      isFloating: true,
      isCancelled: isCancelled,
    };
  }

  const start = fmtParts(event.startAt, locale);
  const end = event.endAt !== null && event.endAt !== undefined ? fmtParts(event.endAt, locale) : null;
  const sameDay = !end || end.date === start.date;
  return {
    date: sameDay ? start.date : `${start.date} – ${end?.date}`,
    time: end ? `${start.time}–${end.time}` : start.time,
    zone: TIME_ZONE,
    isFloating: false,
    isCancelled: isCancelled,
  };
}

/** Weekday name for the start day, e.g. "pirmadienis" — empty if unknown. */
export function meetingWeekday(event: DeskCalendarEvent, locale = "lt-LT"): string {
  const ms =
    event.startAt !== null && event.startAt !== undefined
      ? event.startAt
      : Date.parse(`${event.startLocal}${event.startLocal.includes("T") ? "" : "T00:00:00"}`);
  if (!Number.isFinite(ms)) return "";
  const options: Intl.DateTimeFormatOptions = { weekday: "long" };
  // A floating time has no zone to render it in — take the date as written.
  if (event.startAt !== null && event.startAt !== undefined) options.timeZone = TIME_ZONE;
  return new Intl.DateTimeFormat(locale, options).format(new Date(ms));
}

/** "2026-09-22 09:00–10:00" — one line for lists and tooltips. */
export function meetingOneLiner(event: DeskCalendarEvent, locale = "lt-LT"): string {
  const shown = describeMeeting(event, locale);
  return [shown.date, shown.time].filter(Boolean).join(" ") + (shown.isFloating && shown.zone ? ` (${shown.zone})` : "");
}

export { TIME_ZONE as MEETING_TIME_ZONE };
